import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Plus, Save, Sparkles, Wand2, Image as ImageIcon, Trash2, ArrowLeft, Loader2,
  AlertTriangle, Calendar, FileText, Send, Eye, ChevronRight, Gauge, Lightbulb,
} from "lucide-react";
import api from "../../lib/api";
import DashboardLayout from "../../components/dashboard/DashboardLayout";
import MediaCenterModal from "../../components/admin/MediaCenterModal";
import { renderMarkdown, stripMarkdown } from "../../lib/markdown";

// ---------- Helpers ----------
const STATUS_COLORS = {
  draft:     { bg: "bg-neutral-500/15", text: "text-neutral-300", label: "Draft" },
  scheduled: { bg: "bg-sky-500/15",     text: "text-sky-300",     label: "Scheduled" },
  published: { bg: "bg-emerald-500/15", text: "text-emerald-300", label: "Published" },
};

// Convert an ISO string to a value suitable for <input type="datetime-local"> (no seconds/TZ)
const isoToLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const localInputToIso = (v) => (v ? new Date(v).toISOString() : null);

// ---------- Reminder banner ----------
const ReminderBanner = ({ data, onDismiss }) => {
  if (!data?.stale) return null;
  return (
    <div data-testid="spotlight-reminder"
      className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 flex items-start gap-3 mb-6">
      <AlertTriangle size={18} className="text-amber-400 shrink-0 mt-0.5" />
      <div className="flex-1">
        <div className="text-white font-semibold text-[14px]">Spotlight is going quiet</div>
        <div className="text-amber-100/80 text-[13px] mt-0.5">{data.message}</div>
      </div>
      <button onClick={onDismiss} className="text-amber-300/80 hover:text-white text-[12px]">Dismiss</button>
    </div>
  );
};

// ---------- List view ----------
const PostRow = ({ post, onEdit, onDelete }) => {
  const s = STATUS_COLORS[post.status] || STATUS_COLORS.draft;
  return (
    <div className="rounded-xl border border-[#2A2A2A] bg-[#181818] p-4 flex items-center gap-4 hover:border-[#3a3a3a] transition-colors"
         data-testid={`post-row-${post.slug}`}>
      <div className="w-16 h-16 rounded-lg bg-[#1f1f1f] overflow-hidden shrink-0">
        {post.hero_image_url
          ? <img src={post.hero_image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
          : <div className="w-full h-full flex items-center justify-center text-neutral-600"><ImageIcon size={20} /></div>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className={`inline-block text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full ${s.bg} ${s.text}`}>
            {s.label}
          </span>
          <span className="text-[11px] text-neutral-500">/spotlight/{post.slug}</span>
        </div>
        <h3 className="text-white font-semibold text-[15px] truncate">{post.title}</h3>
        {post.excerpt && <p className="text-neutral-400 text-[13px] truncate mt-0.5">{post.excerpt}</p>}
      </div>
      <div className="flex items-center gap-1">
        <button onClick={() => onEdit(post)} data-testid={`edit-${post.slug}`}
          className="p-2 text-neutral-400 hover:text-white hover:bg-[#232323] rounded-lg"><FileText size={16} /></button>
        <button onClick={() => onDelete(post)} data-testid={`delete-${post.slug}`}
          className="p-2 text-neutral-400 hover:text-red-400 hover:bg-[#232323] rounded-lg"><Trash2 size={16} /></button>
      </div>
    </div>
  );
};

// ---------- AI panel ----------
const AiPanel = ({ post, onApplyDraft }) => {
  const [topics, setTopics] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [genTopic, setGenTopic] = useState("");
  const [auditLoading, setAuditLoading] = useState(false);
  const [audit, setAudit] = useState(null);

  const suggestTopics = async () => {
    setTopicsLoading(true);
    try {
      const { data } = await api.post("/admin/spotlight/ai/topics", { lang: post.lang || "sv" });
      setTopics(data.topics || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not fetch topics");
    } finally { setTopicsLoading(false); }
  };

  const generate = async (topic) => {
    if (!topic) return;
    setGenLoading(true);
    try {
      const { data } = await api.post("/admin/spotlight/ai/generate", {
        topic,
        lang: post.lang || "sv",
        length_words: 700,
        keywords: ["Boka DJ Stockholm", "Eventunderhållning Stockholm"],
      });
      onApplyDraft(data);
      toast.success("Draft generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally { setGenLoading(false); }
  };

  const audit_now = async () => {
    if (!post.body_md?.trim()) {
      toast.error("Write some body content first");
      return;
    }
    setAuditLoading(true);
    try {
      const { data } = await api.post("/admin/spotlight/ai/seo-audit", {
        title: post.title,
        excerpt: post.excerpt,
        body_md: post.body_md,
        lang: post.lang || "sv",
      });
      setAudit(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Audit failed");
    } finally { setAuditLoading(false); }
  };

  const gradeColor = (grade) => ({
    A: "text-emerald-400", B: "text-emerald-300", C: "text-yellow-400",
    D: "text-orange-400", F: "text-red-400",
  }[grade] || "text-neutral-400");

  return (
    <div className="space-y-4">
      {/* Generate */}
      <div className="rounded-2xl border border-[#2A2A2A] bg-[#181818] p-5">
        <div className="flex items-center gap-2 mb-3">
          <Wand2 size={16} className="text-[#FF7A2F]" />
          <h4 className="text-white font-semibold text-[14px]">Generate with AI</h4>
        </div>
        <input value={genTopic} onChange={(e) => setGenTopic(e.target.value)}
          placeholder="Topic (e.g. Vad kostar en bröllops-DJ i Stockholm 2026)"
          data-testid="ai-topic-input"
          className="w-full bg-[#111] border border-[#2a2a2a] rounded-lg px-3 py-2 text-[14px] text-white placeholder:text-neutral-600 outline-none focus:border-[#FF5C00]" />
        <button onClick={() => generate(genTopic)} disabled={genLoading || !genTopic.trim()}
          data-testid="ai-generate"
          className="mt-3 w-full flex items-center justify-center gap-2 bg-[#1A8A4A] hover:bg-[#20A458] disabled:opacity-50 text-white font-semibold py-2 rounded-lg text-[14px]">
          {genLoading ? <><Loader2 size={14} className="animate-spin" /> Writing draft…</> : <><Sparkles size={14} /> Generate full draft</>}
        </button>
      </div>

      {/* Topic suggestions */}
      <div className="rounded-2xl border border-[#2A2A2A] bg-[#181818] p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Lightbulb size={16} className="text-[#FF7A2F]" />
            <h4 className="text-white font-semibold text-[14px]">Trending topics</h4>
          </div>
          <button onClick={suggestTopics} disabled={topicsLoading}
            data-testid="ai-suggest-topics"
            className="text-[12px] text-[#FF7A2F] hover:underline flex items-center gap-1">
            {topicsLoading ? <Loader2 size={12} className="animate-spin" /> : null} Refresh
          </button>
        </div>
        {topics.length === 0 ? (
          <button onClick={suggestTopics} disabled={topicsLoading} data-testid="ai-topics-load"
            className="w-full py-2.5 rounded-lg border border-dashed border-[#333] hover:border-[#FF5C00] text-neutral-400 hover:text-white text-[13px] transition-colors">
            {topicsLoading ? "Thinking…" : "Suggest 5 trending topics"}
          </button>
        ) : (
          <ul className="space-y-2">
            {topics.map((t, i) => (
              <li key={i} className="text-[13px]">
                <button onClick={() => setGenTopic(t.title)}
                  data-testid={`ai-topic-${i}`}
                  className="w-full text-left rounded-lg p-2.5 bg-[#111] hover:bg-[#1a1a1a] border border-transparent hover:border-[#2a2a2a] transition-colors">
                  <div className="text-white font-medium">{t.title}</div>
                  {t.angle && <div className="text-neutral-500 text-[12px] mt-1">{t.angle}</div>}
                  {t.keyword && <div className="text-[#FF7A2F] text-[11px] mt-1">→ {t.keyword}</div>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* SEO audit */}
      <div className="rounded-2xl border border-[#2A2A2A] bg-[#181818] p-5">
        <div className="flex items-center gap-2 mb-3">
          <Gauge size={16} className="text-[#FF7A2F]" />
          <h4 className="text-white font-semibold text-[14px]">SEO audit</h4>
        </div>
        <button onClick={audit_now} disabled={auditLoading}
          data-testid="ai-audit-run"
          className="w-full py-2.5 rounded-lg border border-[#2a2a2a] hover:border-[#FF5C00] text-white text-[13px] font-medium transition-colors flex items-center justify-center gap-2">
          {auditLoading ? <><Loader2 size={14} className="animate-spin" /> Auditing…</> : <>Run audit</>}
        </button>
        {audit && (
          <div className="mt-4" data-testid="ai-audit-result">
            <div className="flex items-baseline gap-3 mb-3">
              <div className={`text-[52px] leading-none font-black ${gradeColor(audit.grade)}`}>{audit.score}</div>
              <div>
                <div className={`text-[16px] font-bold ${gradeColor(audit.grade)}`}>Grade {audit.grade}</div>
                <div className="text-[11px] text-neutral-500">/ 100</div>
              </div>
            </div>
            {audit.summary && <p className="text-[13px] text-neutral-300 mb-3">{audit.summary}</p>}
            {audit.suggestions?.length > 0 && (
              <ul className="space-y-2">
                {audit.suggestions.map((s, i) => (
                  <li key={i} className="text-[12px] text-neutral-400 flex items-start gap-2">
                    <ChevronRight size={12} className="text-[#FF7A2F] shrink-0 mt-0.5" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- Editor ----------
const EMPTY = {
  id: null, slug: "", title: "", excerpt: "", body_md: "",
  hero_image_url: null, hero_alt: "", author: "Talentsio",
  tags: [], lang: "sv", status: "draft", publish_at: null,
};

const Editor = ({ post, onSaved, onBack }) => {
  const [form, setForm] = useState(post);
  const [saving, setSaving] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => setForm(post), [post]);

  const save = async (opts = {}) => {
    setSaving(true);
    try {
      const payload = {
        title: form.title || "Untitled",
        slug: form.slug || undefined,
        excerpt: form.excerpt,
        body_md: form.body_md,
        hero_image_url: form.hero_image_url,
        hero_alt: form.hero_alt,
        author: form.author,
        tags: form.tags,
        lang: form.lang,
        publish_at: form.publish_at,
        status: opts.status || form.status,
      };
      let data;
      if (form.id) {
        ({ data } = await api.put(`/admin/spotlight/${form.id}`, payload));
      } else {
        ({ data } = await api.post("/admin/spotlight", payload));
      }
      setForm(data);
      toast.success(opts.status === "published" ? "Published" : "Saved");
      onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const applyDraft = (d) => {
    setForm((f) => ({
      ...f,
      title: d.title || f.title,
      excerpt: d.excerpt || f.excerpt,
      body_md: d.body_md || f.body_md,
      tags: (d.tags && d.tags.length) ? d.tags : f.tags,
    }));
  };

  const pickImage = ({ url, alt }) => setForm((f) => ({ ...f, hero_image_url: url, hero_alt: alt || f.hero_alt }));

  const publishAtLocal = useMemo(() => isoToLocalInput(form.publish_at), [form.publish_at]);

  const rendered = useMemo(() => renderMarkdown(form.body_md), [form.body_md]);

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
      {/* Left: editor */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="text-neutral-400 hover:text-white flex items-center gap-1 text-[13px]"
            data-testid="editor-back">
            <ArrowLeft size={14} /> Back to list
          </button>
          <div className="flex-1" />
          <button onClick={() => setShowPreview(!showPreview)} className="text-[13px] px-3 py-1.5 rounded-lg border border-[#2a2a2a] text-neutral-300 hover:border-[#3a3a3a] flex items-center gap-1.5"
            data-testid="editor-toggle-preview">
            <Eye size={13} /> {showPreview ? "Edit" : "Preview"}
          </button>
        </div>

        {!showPreview ? (
          <>
            <div className="rounded-2xl border border-[#2A2A2A] bg-[#181818] p-5 space-y-4">
              <input value={form.title} onChange={(e) => upd("title", e.target.value)}
                placeholder="Post title"
                data-testid="editor-title"
                className="w-full bg-transparent text-white text-[28px] font-bold placeholder:text-neutral-600 outline-none" />
              <input value={form.slug} onChange={(e) => upd("slug", e.target.value)}
                placeholder="url-slug-goes-here (auto if left blank)"
                data-testid="editor-slug"
                className="w-full bg-[#111] border border-[#2a2a2a] rounded-lg px-3 py-2 text-[13px] text-neutral-300 placeholder:text-neutral-600 outline-none focus:border-[#FF5C00]" />
              <textarea value={form.excerpt} onChange={(e) => upd("excerpt", e.target.value)}
                rows={2} placeholder="Excerpt (used in meta description and on the card)"
                data-testid="editor-excerpt"
                className="w-full bg-[#111] border border-[#2a2a2a] rounded-lg px-3 py-2 text-[14px] text-neutral-200 placeholder:text-neutral-600 outline-none focus:border-[#FF5C00] resize-none" />

              {/* Hero image */}
              <div>
                <label className="block text-[12px] text-neutral-500 mb-2">Header image</label>
                {form.hero_image_url ? (
                  <div className="relative rounded-xl overflow-hidden border border-[#2a2a2a] group">
                    <img src={form.hero_image_url} alt={form.hero_alt} className="w-full aspect-[16/9] object-cover" />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button onClick={() => setMediaOpen(true)} className="px-3 py-1.5 rounded-lg bg-white text-black text-[13px] font-semibold" data-testid="editor-replace-image">Replace</button>
                      <button onClick={() => upd("hero_image_url", null)} className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-[13px] font-semibold" data-testid="editor-remove-image">Remove</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => setMediaOpen(true)} data-testid="editor-add-image"
                    className="w-full border-2 border-dashed border-[#333] hover:border-[#FF5C00] rounded-xl p-8 text-neutral-400 hover:text-white transition-colors flex flex-col items-center gap-2">
                    <ImageIcon size={26} /> <span className="text-[13px]">Add a header image (upload, search, or generate)</span>
                  </button>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-[#2A2A2A] bg-[#181818] p-5">
              <label className="block text-[12px] text-neutral-500 mb-2">Body (Markdown)</label>
              <textarea value={form.body_md} onChange={(e) => upd("body_md", e.target.value)}
                rows={20} placeholder="# Your article\n\nWrite in Markdown. Use ## for section headings."
                data-testid="editor-body"
                className="w-full bg-[#111] border border-[#2a2a2a] rounded-lg px-4 py-3 text-[14px] text-white placeholder:text-neutral-600 outline-none focus:border-[#FF5C00] font-mono resize-y leading-relaxed" />
              <p className="text-[11px] text-neutral-500 mt-2">{form.body_md.length} chars · {form.body_md.split(/\s+/).filter(Boolean).length} words</p>
            </div>
          </>
        ) : (
          <div className="rounded-2xl border border-[#2A2A2A] bg-[#181818] p-8">
            <h1 className="text-white text-[36px] font-extrabold leading-tight mb-3">{form.title || "Untitled"}</h1>
            {form.excerpt && <p className="text-neutral-300 text-[17px] mb-6">{form.excerpt}</p>}
            {form.hero_image_url && <img src={form.hero_image_url} alt={form.hero_alt} className="w-full aspect-[16/9] object-cover rounded-xl mb-6" />}
            <div className="spotlight-prose" dangerouslySetInnerHTML={{ __html: rendered }} />
          </div>
        )}
      </div>

      {/* Right: sidebar */}
      <div className="space-y-4">
        <div className="rounded-2xl border border-[#2A2A2A] bg-[#181818] p-5 space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <span className={`inline-block text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full ${(STATUS_COLORS[form.status] || STATUS_COLORS.draft).bg} ${(STATUS_COLORS[form.status] || STATUS_COLORS.draft).text}`}>
              {(STATUS_COLORS[form.status] || STATUS_COLORS.draft).label}
            </span>
          </div>
          <div>
            <label className="block text-[12px] text-neutral-500 mb-1 flex items-center gap-1"><Calendar size={12} /> Schedule (publish at)</label>
            <input type="datetime-local" value={publishAtLocal}
              onChange={(e) => upd("publish_at", localInputToIso(e.target.value))}
              data-testid="editor-publish-at"
              className="w-full bg-[#111] border border-[#2a2a2a] rounded-lg px-3 py-2 text-[13px] text-white outline-none focus:border-[#FF5C00]" />
            <p className="text-[11px] text-neutral-500 mt-1">Leave blank to keep as draft.</p>
          </div>
          <div>
            <label className="block text-[12px] text-neutral-500 mb-1">Language</label>
            <select value={form.lang} onChange={(e) => upd("lang", e.target.value)}
              data-testid="editor-lang"
              className="w-full bg-[#111] border border-[#2a2a2a] rounded-lg px-3 py-2 text-[13px] text-white outline-none focus:border-[#FF5C00]">
              <option value="sv">Svenska</option>
              <option value="en">English</option>
            </select>
          </div>
          <div>
            <label className="block text-[12px] text-neutral-500 mb-1">Tags (comma separated)</label>
            <input value={form.tags?.join(", ") || ""}
              onChange={(e) => upd("tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))}
              data-testid="editor-tags"
              className="w-full bg-[#111] border border-[#2a2a2a] rounded-lg px-3 py-2 text-[13px] text-white outline-none focus:border-[#FF5C00]" />
          </div>
          <div className="flex flex-col gap-2 pt-2 border-t border-[#2a2a2a]">
            <button onClick={() => save({ status: "draft" })} disabled={saving}
              data-testid="editor-save-draft"
              className="w-full flex items-center justify-center gap-2 bg-[#232323] hover:bg-[#2a2a2a] text-white font-semibold py-2 rounded-lg text-[13px]">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save draft
            </button>
            <button onClick={() => save({ status: form.publish_at && new Date(form.publish_at) > new Date() ? undefined : "published" })}
              disabled={saving}
              data-testid="editor-publish"
              className="w-full flex items-center justify-center gap-2 bg-[#1A8A4A] hover:bg-[#20A458] text-white font-semibold py-2 rounded-lg text-[13px]">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {form.publish_at && new Date(form.publish_at) > new Date() ? "Schedule" : "Publish now"}
            </button>
          </div>
        </div>

        <AiPanel post={form} onApplyDraft={applyDraft} />
      </div>

      <MediaCenterModal open={mediaOpen} onClose={() => setMediaOpen(false)} onSelect={pickImage} />
    </div>
  );
};

// ---------- Root ----------
const AdminSpotlight = () => {
  const nav = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list view
  const [reminder, setReminder] = useState(null);
  const [reminderDismissed, setReminderDismissed] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [listR, remR] = await Promise.all([
        api.get("/admin/spotlight"),
        api.get("/admin/spotlight/reminder"),
      ]);
      setPosts(listR.data.posts || []);
      setReminder(remR.data || null);
    } catch (e) {
      toast.error("Could not load posts");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ ...EMPTY });
  const editPost = (p) => setEditing({ ...EMPTY, ...p });
  const backToList = () => { setEditing(null); load(); };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try {
      await api.delete(`/admin/spotlight/${p.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-[1200px] mx-auto px-6 py-8">
        {!editing && (
          <>
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="flex items-center gap-2 text-[11px] tracking-[0.14em] text-[#FF7A2F] font-bold uppercase mb-2">
                  <Sparkles size={12} /> SPOTLIGHT ADMIN
                </div>
                <h1 className="text-white text-[28px] font-bold">Editorial CMS</h1>
                <p className="text-neutral-400 text-[14px] mt-1">Publish articles, schedule posts and audit SEO from one place.</p>
              </div>
              <button onClick={startNew} data-testid="new-post"
                className="inline-flex items-center gap-2 bg-[#1A8A4A] hover:bg-[#20A458] text-white font-semibold px-4 py-2.5 rounded-xl">
                <Plus size={16} /> New post
              </button>
            </div>
            {!reminderDismissed && <ReminderBanner data={reminder} onDismiss={() => setReminderDismissed(true)} />}
            {loading ? (
              <div className="text-neutral-500 text-[14px]">Loading…</div>
            ) : posts.length === 0 ? (
              <div className="rounded-2xl border border-[#2A2A2A] bg-[#181818] p-12 text-center" data-testid="admin-empty">
                <FileText size={40} className="text-neutral-500 mx-auto mb-3" />
                <p className="text-white text-[16px] font-semibold mb-1">No posts yet</p>
                <p className="text-neutral-500 text-[13px] mb-5">Start with an AI-generated draft &mdash; hit &ldquo;New post&rdquo; above.</p>
                <button onClick={startNew} className="inline-flex items-center gap-2 bg-[#1A8A4A] hover:bg-[#20A458] text-white font-semibold px-4 py-2 rounded-xl text-[13px]">
                  <Plus size={14} /> Create your first post
                </button>
              </div>
            ) : (
              <div className="space-y-3" data-testid="admin-post-list">
                {posts.map((p) => <PostRow key={p.id} post={p} onEdit={editPost} onDelete={remove} />)}
              </div>
            )}
          </>
        )}
        {editing && <Editor post={editing} onSaved={() => {}} onBack={backToList} />}
      </div>
    </DashboardLayout>
  );
};

export default AdminSpotlight;
